import { ApexOptions } from "apexcharts";
import { format } from "date-fns";
import React, { useMemo } from "react";
import { Loading } from "react-admin";
import Chart from "react-apexcharts";
import {
  addSigned,
  buildThreeDayCategoriesUTC,
  computeAveragePrice,
  computeHourModeByKey,
  expandSymmetric,
  formatPrice,
  hourKeyUTC,
  keepLatestByHour,
  toNum,
} from "./helpers";
import useEcpFcrData from "./hooks/useEcpFcrData";
import { BusinessType, RegularBid, YearlyPlanPoint } from "./types";
import { Alert, AlertTitle } from "@mui/material";

export type StackedBidsChartProps = {
  deliveryDate: Date;
  businessType: BusinessType;
  height?: number | string;
  width?: number | string;
  seed?: number;
};

const StackedBidsChart: React.FC<StackedBidsChartProps> = ({
  deliveryDate,
  businessType,
  height = 1000,
  width = "100%",
}) => {
  const { regularBids, loadingRegularBids, errorRegularBids, yearlyBids, yearlyErrors, yearlyLoading } = useEcpFcrData({
    businessType,
    deliveryDate: new Date(deliveryDate),
  });

  const anyYearlyLoading = useMemo(() => yearlyLoading.some(Boolean), [yearlyLoading]);
  const anyYearlyError = useMemo(() => yearlyErrors.some(Boolean), [yearlyErrors]);

  const categories = useMemo(() => buildThreeDayCategoriesUTC(new Date(deliveryDate)), [deliveryDate]);

  // Map of normalized UTC-hour -> index
  const indexByHourKey = useMemo(() => {
    const m = new Map<string, number>();
    categories.forEach((c, i) => m.set(hourKeyUTC(c), i));
    return m;
  }, [categories]);

  const yearlyExpanded = useMemo<YearlyPlanPoint[]>(
    () => keepLatestByHour(expandSymmetric<YearlyPlanPoint>(yearlyBids)),
    [yearlyBids],
  );

  const regularExpanded = useMemo<RegularBid[]>(
    () => keepLatestByHour(expandSymmetric<RegularBid>(regularBids as RegularBid[])),
    [regularBids],
  );

  const daySeparators = useMemo(() => {
    const lines = [];
    for (let i = 1; i < categories.length; i++) {
      if (i % 24 === 0) {
        const xVal = new Date(categories[i]).getTime(); // align with datetime x-axis
        lines.push({
          x: xVal,
          borderColor: "#000",
          borderWidth: 2,
          strokeDashArray: 4,
        });
      }
    }
    return lines;
  }, [categories]);

  const rowsByHour = useMemo(() => {
    const m = new Map<string, RegularBid[]>();
    for (const r of regularExpanded as RegularBid[]) {
      const k = hourKeyUTC(r.start_date);
      const arr = m.get(k);
      if (arr) arr.push(r);
      else m.set(k, [r]);
    }
    return m;
  }, [regularExpanded]);

  const hourModeByKey = useMemo(() => computeHourModeByKey(regularExpanded as RegularBid[]), [regularExpanded]);

  const seriesData = useMemo(() => {
    const yearlyUp = new Array(categories.length).fill(0);
    const regularUp = new Array(categories.length).fill(0);
    const yearlyDown = new Array(categories.length).fill(0);
    const regularDown = new Array(categories.length).fill(0);

    // Yearly
    for (const y of yearlyExpanded) {
      const i = indexByHourKey.get(hourKeyUTC(y.start_date));
      const q = toNum(y.quantity_mw);
      // use addSigned against the correct target array
      if (y.direction === "UPWARD") addSigned(yearlyUp, i, q, "UPWARD");
      else if (y.direction === "DOWNWARD") addSigned(yearlyDown, i, q, "DOWNWARD");
    }

    // Regular with hour-level mode
    for (const r of regularExpanded as RegularBid[]) {
      const key = hourKeyUTC(r.start_date);
      const i = indexByHourKey.get(key);
      const mode = hourModeByKey.get(key) ?? "accepted";
      const value = mode === "offered" ? toNum(r.offered_quantity_mw) : toNum(r.accepted_quantity_mw);
      if (r.direction === "UPWARD") addSigned(regularUp, i, value, "UPWARD");
      else if (r.direction === "DOWNWARD") addSigned(regularDown, i, value, "DOWNWARD");
    }

    return { yearlyUp, regularUp, yearlyDown, regularDown };
  }, [categories.length, indexByHourKey, yearlyExpanded, regularExpanded, hourModeByKey]);

  const options: ApexOptions = useMemo(
    () => ({
      chart: { type: "bar", stacked: true, height, toolbar: { show: true }, animations: { enabled: true } },
      plotOptions: { bar: { columnWidth: "70%", rangeBarOverlap: false } },
      dataLabels: { enabled: false },
      stroke: { show: false },
      annotations: {
        xaxis: daySeparators,
        // add a black line at y=0
        yaxis: [
          {
            y: 0,
            borderColor: "#000000",
            borderWidth: 2,
            strokeDashArray: 0,
          },
        ],
      },
      xaxis: {
        type: "datetime",
        categories,
        tickAmount: 24,
        labels: {
          rotate: -60,
          datetimeUTC: true,
          style: { fontSize: "12px" },
          formatter: (value, timestamp) => {
            const ms = typeof timestamp === "number" ? timestamp : Number(value);
            if (!Number.isFinite(ms)) return String(value);
            const d = new Date(ms);
            return format(d, "dd/MM/yy HH:mm");
          },
        },
      },
      yaxis: { title: { text: "MW" }, labels: { formatter: (v: number) => `${v}` } },
      colors: ["#7E57C2", "#42A5F5", "#7E57C2", "#42A5F5"],
      legend: { position: "top" },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (val: number) => `${Math.abs(val).toFixed(2)} MW` },
        custom: ({ series, dataPointIndex, w }) => {
          const timestamp = w.globals.seriesX[0][dataPointIndex];
          const date = new Date(timestamp);
          const dateStr = format(date, "dd/MM/yyyy HH:mm");

          const names = w.globals.seriesNames as string[];
          const y0 = series[0]?.[dataPointIndex] ?? 0;
          const y1 = series[1]?.[dataPointIndex] ?? 0;
          const y2 = series[2]?.[dataPointIndex] ?? 0;
          const y3 = series[3]?.[dataPointIndex] ?? 0;
          const yUp = y0 + y1;
          const yDownAbs = Math.abs(y2 + y3);
          const totalAbs = (Math.abs(yUp) + Math.abs(yDownAbs)).toFixed(2);

          const hourKey = hourKeyUTC(new Date(timestamp));
          const mode = hourModeByKey.get(hourKey) ?? "accepted";
          const rowsAtHour = rowsByHour.get(hourKey) ?? [];
          const rowsUp = rowsAtHour.filter((r) => r.direction === "UPWARD");
          const rowsDown = rowsAtHour.filter((r) => r.direction === "DOWNWARD");

          const upOff = computeAveragePrice(rowsUp, "offered_price_eur", "offered_quantity_mw");
          const upAcc = computeAveragePrice(rowsUp, "accepted_price_eur", "accepted_quantity_mw");
          const dnOff = computeAveragePrice(rowsDown, "offered_price_eur", "offered_quantity_mw");
          const dnAcc = computeAveragePrice(rowsDown, "accepted_price_eur", "accepted_quantity_mw");

          return `
            <div style="padding:8px 10px; max-width: 320px;">
              <div><strong>${dateStr}</strong></div>
              <div style="opacity:0.8; margin-top:2px;">Mode: <strong>${mode.toUpperCase()}</strong></div>
              <div style="margin-top:6px;">
                <div>${names[0]}: ${y0.toFixed(2)} MW</div>
                <div>${names[1]}: ${y1.toFixed(2)} MW</div>
                <div><strong>Up total:</strong> ${yUp.toFixed(2)} MW</div>
                <hr />
                <div>${names[2]}: ${Math.abs(y2).toFixed(2)} MW</div>
                <div>${names[3]}: ${Math.abs(y3).toFixed(2)} MW</div>
                <div><strong>Down total:</strong> ${yDownAbs.toFixed(2)} MW</div>
              </div>
  
              <div style="margin-top:8px;">
                <div style="font-weight:600;margin-bottom:4px;">Regular prices</div>
                <div>Up — Offered: <strong>${formatPrice(upOff)}</strong> • Accepted: <strong>${formatPrice(
            upAcc,
          )}</strong></div>
                <div>Down — Offered: <strong>${formatPrice(dnOff)}</strong> • Accepted: <strong>${formatPrice(
            dnAcc,
          )}</strong></div>
              </div>
  
              <div style="margin-top:6px;">Absolute total: <strong>${totalAbs} MW</strong></div>
            </div>`;
        },
      },
    }),
    [categories, height, daySeparators, hourModeByKey, rowsByHour],
  );

  const series = useMemo(
    () => [
      { name: "Reserve Plan (Up)", data: seriesData.yearlyUp },
      { name: "Hourly (Up)", data: seriesData.regularUp },
      { name: "Reserve Plan (Down)", data: seriesData.yearlyDown },
      { name: "Hourly (Down)", data: seriesData.regularDown },
    ],
    [seriesData],
  );

  if (loadingRegularBids || anyYearlyLoading) return <Loading />;
  if (errorRegularBids || anyYearlyError) {
    const firstYearlyError = yearlyErrors.find(Boolean);
    const err = errorRegularBids || firstYearlyError || "Unknown error";
    return (
      <Alert severity="error">
        <AlertTitle>Failed to load data.</AlertTitle>
        {String(err)}
      </Alert>
    );
  }

  return <Chart options={options} series={series as any} type="bar" height={height} width={width} />;
};

export default StackedBidsChart;
